import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, useTheme } from '@mui/material';
import {
  ArrowForward,
  Rocket,
  CheckCircle,
  Speed,
  Psychology,
  AutoAwesome,
  TouchApp,
  SettingsApplications,
  Insights,
  Check,
  GitHub
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const LandingPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Hero animation
  const [heroVisible, setHeroVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setHeroVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Box sx={{
      width: '100vw',
      position: 'relative',
      left: '50%',
      right: '50%',
      marginLeft: '-50vw',
      marginRight: '-50vw',
      overflowX: 'hidden'
    }}>
      {/* SECTION 1: HERO */}
      <Box sx={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        position: 'relative',
        background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)',
        overflow: 'hidden'
      }}>
        {/* Animated background elements */}
        <Box
          component={motion.div}
          animate={{
            opacity: [0.1, 0.2, 0.1],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: 'reverse'
          }}
          sx={{
            position: 'absolute',
            top: '10%',
            right: '5%',
            width: '40%',
            height: '40%',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0, 229, 255, 0.2) 0%, rgba(0, 229, 255, 0) 70%)',
            filter: 'blur(80px)',
            zIndex: 0
          }}
        />

        <Box
          component={motion.div}
          animate={{
            opacity: [0.1, 0.2, 0.1],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 10,
            delay: 2,
            repeat: Infinity,
            repeatType: 'reverse'
          }}
          sx={{
            position: 'absolute',
            bottom: '10%',
            left: '5%',
            width: '30%',
            height: '30%',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(41, 121, 255, 0.2) 0%, rgba(41, 121, 255, 0) 70%)',
            filter: 'blur(80px)',
            zIndex: 0
          }}
        />

        {/* Animated particles */}
        {[...Array(30)].map((_, i) => (
          <Box
            key={i}
            component={motion.div}
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              opacity: Math.random() * 0.5 + 0.1,
              scale: Math.random() * 0.5 + 0.5
            }}
            animate={{
              x: [
                Math.random() * window.innerWidth,
                Math.random() * window.innerWidth,
                Math.random() * window.innerWidth
              ],
              y: [
                Math.random() * window.innerHeight,
                Math.random() * window.innerHeight,
                Math.random() * window.innerHeight
              ],
              opacity: [
                Math.random() * 0.5 + 0.1,
                Math.random() * 0.5 + 0.3,
                Math.random() * 0.5 + 0.1
              ]
            }}
            transition={{
              duration: 20 + Math.random() * 30,
              repeat: Infinity,
              repeatType: 'reverse'
            }}
            sx={{
              position: 'absolute',
              width: 4 + Math.random() * 6,
              height: 4 + Math.random() * 6,
              borderRadius: '50%',
              background: `rgba(${Math.random() > 0.5 ? '0, 229, 255' : '255, 0, 229'}, ${Math.random() * 0.7 + 0.3})`,
              boxShadow: `0 0 10px rgba(${Math.random() > 0.5 ? '0, 229, 255' : '255, 0, 229'}, 0.8)`,
              zIndex: 1
            }}
          />
        ))}

        {/* Hero content */}
        <Box sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 4, md: 10 },
          zIndex: 2
        }}>
          {/* Left side - Text content */}
          <Box
            component={motion.div}
            initial={{ opacity: 0, x: -100 }}
            animate={{
              opacity: heroVisible ? 1 : 0,
              x: heroVisible ? 0 : -100
            }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            sx={{
              width: { xs: '100%', md: '50%' },
              pr: { xs: 0, md: 8 },
              mb: { xs: 6, md: 0 }
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '3.5rem', md: '5rem', lg: '6rem' },
                fontWeight: 800,
                mb: 4,
                background: 'linear-gradient(45deg, #00e5ff, #2979ff, #00e5ff)',
                backgroundSize: '200% 200%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'gradient 8s ease infinite',
                '@keyframes gradient': {
                  '0%': { backgroundPosition: '0% 50%' },
                  '50%': { backgroundPosition: '100% 50%' },
                  '100%': { backgroundPosition: '0% 50%' }
                }
              }}
            >
              Taskly.AI
            </Typography>

            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.5rem', md: '2rem', lg: '2.5rem' },
                fontWeight: 400,
                color: 'rgba(255, 255, 255, 0.8)',
                mb: 6,
                maxWidth: '600px'
              }}
            >
              Supercharge your productivity with intelligent AI-powered task management
            </Typography>

            <Box sx={{
              display: 'flex',
              gap: 3,
              flexWrap: { xs: 'wrap', sm: 'nowrap' }
            }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<Rocket />}
                onClick={() => navigate('/register')}
                sx={{
                  py: 2.5,
                  px: 5,
                  background: 'linear-gradient(45deg, #00e5ff 30%, #2979ff 90%)',
                  boxShadow: '0 0 20px rgba(0, 229, 255, 0.5)',
                  borderRadius: '50px',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  '&:hover': {
                    boxShadow: '0 0 30px rgba(0, 229, 255, 0.8)',
                    transform: 'translateY(-3px) scale(1.02)',
                    transition: 'all 0.3s ease'
                  }
                }}
              >
                Get Started
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/login')}
                sx={{
                  py: 2.5,
                  px: 5,
                  borderColor: '#00e5ff',
                  borderRadius: '50px',
                  fontSize: '1.2rem',
                  borderWidth: '2px',
                  color: '#00e5ff',
                  '&:hover': {
                    borderColor: '#2979ff',
                    backgroundColor: 'rgba(0, 229, 255, 0.1)',
                    transform: 'translateY(-3px)',
                    transition: 'all 0.3s ease'
                  }
                }}
              >
                Login
              </Button>
            </Box>
          </Box>

          {/* Right side - Image */}
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 100 }}
            animate={{
              opacity: heroVisible ? 1 : 0,
              y: heroVisible ? 0 : 100
            }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
            sx={{
              width: { xs: '100%', md: '50%' },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Box
              component={motion.div}
              animate={{
                y: ['-5%', '5%'],
                rotate: ['-2deg', '2deg']
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                repeatType: 'reverse',
                ease: "easeInOut"
              }}
              sx={{ position: 'relative', width: '100%', maxWidth: '600px' }}
            >
              {/* Glowing effect behind image */}
              <Box
                component={motion.div}
                animate={{
                  boxShadow: [
                    '0 0 30px rgba(0, 229, 255, 0.3)',
                    '0 0 50px rgba(0, 229, 255, 0.5)',
                    '0 0 30px rgba(0, 229, 255, 0.3)'
                  ]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  repeatType: 'reverse'
                }}
                sx={{
                  position: 'absolute',
                  top: '5%',
                  left: '5%',
                  width: '90%',
                  height: '90%',
                  borderRadius: '20px',
                  filter: 'blur(20px)',
                  zIndex: -1
                }}
              />

              <Box
                component="img"
                src="/dashboard.png"
                alt="Dashboard Preview"
                sx={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '20px',
                  boxShadow: '0 20px 80px rgba(0, 0, 0, 0.5)',
                  border: '2px solid rgba(255, 255, 255, 0.1)',
                  transform: 'perspective(1000px) rotateY(-5deg) rotateX(5deg)',
                  transition: 'all 0.5s ease',
                  '&:hover': {
                    transform: 'perspective(1000px) rotateY(0deg) rotateX(0deg)',
                  }
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* SECTION 2: FEATURES */}
      <Box sx={{
        width: '100%',
        minHeight: '90vh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1a 100%)',
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 10, md: 15 }
      }}>
        {/* Background elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: 'radial-gradient(rgba(0, 229, 255, 0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px',
            opacity: 0.3,
            zIndex: 0
          }}
        />

        <Box
          component={motion.div}
          animate={{
            opacity: [0.05, 0.1, 0.05],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: 'reverse'
          }}
          sx={{
            position: 'absolute',
            top: '30%',
            right: '-10%',
            width: '40%',
            height: '40%',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0, 229, 255, 0.15) 0%, rgba(0, 229, 255, 0) 70%)',
            filter: 'blur(80px)',
            zIndex: 0
          }}
        />

        {/* Content */}
        <Box sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: { xs: 4, md: 10 },
          position: 'relative',
          zIndex: 1
        }}>
          {/* Section header */}
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            sx={{
              textAlign: 'center',
              mb: { xs: 8, md: 12 },
              maxWidth: '800px'
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 700,
                mb: 3,
                background: 'linear-gradient(45deg, #00e5ff, #2979ff, #00e5ff)',
                backgroundSize: '200% 200%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'gradient 8s ease infinite'
              }}
            >
              Key Features
            </Typography>

            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                color: 'rgba(255, 255, 255, 0.7)',
                maxWidth: '700px',
                mx: 'auto'
              }}
            >
              Discover how our AI-powered task management system can transform your productivity
            </Typography>
          </Box>

          {/* Features grid */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr 1fr' },
            gap: 5,
            width: '100%',
            maxWidth: '1400px'
          }}>
            {/* Feature 1 */}
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              sx={{
                background: 'rgba(30, 30, 40, 0.6)',
                backdropFilter: 'blur(10px)',
                borderRadius: '24px',
                p: 5,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '4px',
                  background: theme.palette.success.main,
                  opacity: 0.8
                }
              }}
            >
              <Box sx={{
                mb: 3,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(5px)'
              }}>
                <CheckCircle sx={{ fontSize: '2.5rem', color: theme.palette.success.main }} />
              </Box>

              <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                Task Management
              </Typography>

              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Create, organize, and track your tasks with ease. Set priorities, due dates, and monitor progress.
              </Typography>
            </Box>

            {/* Feature 2 */}
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              sx={{
                background: 'rgba(30, 30, 40, 0.6)',
                backdropFilter: 'blur(10px)',
                borderRadius: '24px',
                p: 5,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '4px',
                  background: theme.palette.secondary.main,
                  opacity: 0.8
                }
              }}
            >
              <Box sx={{
                mb: 3,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(5px)'
              }}>
                <AutoAwesome sx={{ fontSize: '2.5rem', color: theme.palette.secondary.main }} />
              </Box>

              <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                AI-Powered
              </Typography>

              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Leverage AI to summarize tasks, generate subtasks, and prioritize your workload intelligently.
              </Typography>
            </Box>

            {/* Feature 3 */}
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              sx={{
                background: 'rgba(30, 30, 40, 0.6)',
                backdropFilter: 'blur(10px)',
                borderRadius: '24px',
                p: 5,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '4px',
                  background: theme.palette.warning.main,
                  opacity: 0.8
                }
              }}
            >
              <Box sx={{
                mb: 3,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(5px)'
              }}>
                <Speed sx={{ fontSize: '2.5rem', color: theme.palette.warning.main }} />
              </Box>

              <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                Productivity Insights
              </Typography>

              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Visualize your productivity with intuitive charts and statistics to optimize your workflow.
              </Typography>
            </Box>

            {/* Feature 4 */}
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              sx={{
                background: 'rgba(30, 30, 40, 0.6)',
                backdropFilter: 'blur(10px)',
                borderRadius: '24px',
                p: 5,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '4px',
                  background: theme.palette.info.main,
                  opacity: 0.8
                }
              }}
            >
              <Box sx={{
                mb: 3,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(5px)'
              }}>
                <Psychology sx={{ fontSize: '2.5rem', color: theme.palette.info.main }} />
              </Box>

              <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                Smart Suggestions
              </Typography>

              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Receive intelligent recommendations for task organization and time management.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* SECTION 3: HOW IT WORKS */}
      <Box sx={{
        width: '100%',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)',
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 10, md: 15 }
      }}>
        {/* Background elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: 'linear-gradient(rgba(0, 229, 255, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 229, 255, 0.05) 1px, transparent 1px)',
            backgroundSize: '50px 50px',
            opacity: 0.2,
            zIndex: 0
          }}
        />

        <Box
          component={motion.div}
          animate={{
            opacity: [0.05, 0.1, 0.05],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: 'reverse'
          }}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '-10%',
            width: '40%',
            height: '40%',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0, 229, 255, 0.15) 0%, rgba(0, 229, 255, 0) 70%)',
            filter: 'blur(80px)',
            zIndex: 0
          }}
        />

        {/* Content */}
        <Box sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: { xs: 4, md: 10 },
          position: 'relative',
          zIndex: 1
        }}>
          {/* Section header */}
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            sx={{
              textAlign: 'center',
              mb: { xs: 8, md: 12 },
              maxWidth: '800px'
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 700,
                mb: 3,
                background: 'linear-gradient(45deg, #00e5ff, #2979ff, #00e5ff)',
                backgroundSize: '200% 200%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'gradient 8s ease infinite'
              }}
            >
              How It Works
            </Typography>

            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                color: 'rgba(255, 255, 255, 0.7)',
                maxWidth: '700px',
                mx: 'auto'
              }}
            >
              Get started with Taskly.AI in three simple steps
            </Typography>
          </Box>

          {/* Steps */}
          <Box sx={{
            width: '100%',
            maxWidth: '1200px',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 8, md: 4 },
            justifyContent: 'center',
            alignItems: { xs: 'center', md: 'stretch' }
          }}>
            {/* Step 1 */}
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              sx={{
                width: { xs: '100%', md: '33.33%' },
                maxWidth: '350px',
                position: 'relative'
              }}
            >
              <Box sx={{
                background: 'rgba(30, 30, 40, 0.6)',
                backdropFilter: 'blur(10px)',
                borderRadius: '24px',
                p: 5,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)'
              }}>
                <Box sx={{
                  position: 'absolute',
                  top: '20px',
                  left: '20px',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'linear-gradient(45deg, #00e5ff, #2979ff)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontWeight: 'bold',
                  fontSize: '1.5rem',
                  color: '#fff',
                  boxShadow: '0 0 15px rgba(0, 229, 255, 0.5)'
                }}>
                  1
                </Box>

                <Box sx={{
                  mb: 3,
                  mt: 3,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(5px)'
                }}>
                  <TouchApp sx={{ fontSize: '3.5rem', color: '#00e5ff' }} />
                </Box>

                <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                  Sign Up
                </Typography>

                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Create your free account in seconds. No credit card required to get started with our powerful task management platform.
                </Typography>
              </Box>

              {/* Connector (visible only on desktop) */}
              <Box
                component={motion.div}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.8 }}
                sx={{
                  display: { xs: 'none', md: 'block' },
                  position: 'absolute',
                  top: '50%',
                  right: '-10%',
                  width: '20%',
                  height: '2px',
                  background: 'linear-gradient(90deg, #00e5ff, transparent)',
                  zIndex: 1
                }}
              />
            </Box>

            {/* Step 2 */}
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              sx={{
                width: { xs: '100%', md: '33.33%' },
                maxWidth: '350px',
                position: 'relative'
              }}
            >
              <Box sx={{
                background: 'rgba(30, 30, 40, 0.6)',
                backdropFilter: 'blur(10px)',
                borderRadius: '24px',
                p: 5,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)'
              }}>
                <Box sx={{
                  position: 'absolute',
                  top: '20px',
                  left: '20px',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'linear-gradient(45deg, #00e5ff, #2979ff)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontWeight: 'bold',
                  fontSize: '1.5rem',
                  color: '#fff',
                  boxShadow: '0 0 15px rgba(0, 229, 255, 0.5)'
                }}>
                  2
                </Box>

                <Box sx={{
                  mb: 3,
                  mt: 3,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(5px)'
                }}>
                  <SettingsApplications sx={{ fontSize: '3.5rem', color: '#00e5ff' }} />
                </Box>

                <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                  Set Up Tasks
                </Typography>

                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Create your tasks and let our AI help organize them. Set priorities, deadlines, and categories to keep everything structured.
                </Typography>
              </Box>

              {/* Connector (visible only on desktop) */}
              <Box
                component={motion.div}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 1 }}
                sx={{
                  display: { xs: 'none', md: 'block' },
                  position: 'absolute',
                  top: '50%',
                  right: '-10%',
                  width: '20%',
                  height: '2px',
                  background: 'linear-gradient(90deg, #00e5ff, transparent)',
                  zIndex: 1
                }}
              />
            </Box>

            {/* Step 3 */}
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              sx={{
                width: { xs: '100%', md: '33.33%' },
                maxWidth: '350px'
              }}
            >
              <Box sx={{
                background: 'rgba(30, 30, 40, 0.6)',
                backdropFilter: 'blur(10px)',
                borderRadius: '24px',
                p: 5,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)'
              }}>
                <Box sx={{
                  position: 'absolute',
                  top: '20px',
                  left: '20px',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'linear-gradient(45deg, #00e5ff, #2979ff)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontWeight: 'bold',
                  fontSize: '1.5rem',
                  color: '#fff',
                  boxShadow: '0 0 15px rgba(0, 229, 255, 0.5)'
                }}>
                  3
                </Box>

                <Box sx={{
                  mb: 3,
                  mt: 3,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(5px)'
                }}>
                  <Insights sx={{ fontSize: '3.5rem', color: '#00e5ff' }} />
                </Box>

                <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                  Boost Productivity
                </Typography>

                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Track your progress, receive AI-powered insights, and optimize your workflow to achieve maximum productivity.
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Benefits */}
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.6 }}
            sx={{
              mt: { xs: 10, md: 15 },
              width: '100%',
              maxWidth: '900px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 600,
                mb: 6,
                textAlign: 'center'
              }}
            >
              Why Users Love Taskly.AI
            </Typography>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 4,
              width: '100%'
            }}>
              {/* Benefit 1 */}
              <Box sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 3
              }}>
                <Box sx={{
                  minWidth: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'rgba(0, 229, 255, 0.1)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <Check sx={{ color: '#00e5ff' }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    40% Time Saved
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Users report saving up to 40% of their time on task management compared to traditional methods.
                  </Typography>
                </Box>
              </Box>

              {/* Benefit 2 */}
              <Box sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 3
              }}>
                <Box sx={{
                  minWidth: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'rgba(0, 229, 255, 0.1)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <Check sx={{ color: '#00e5ff' }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Seamless Integration
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Connects with your favorite tools and platforms for a unified productivity experience.
                  </Typography>
                </Box>
              </Box>

              {/* Benefit 3 */}
              <Box sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 3
              }}>
                <Box sx={{
                  minWidth: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'rgba(0, 229, 255, 0.1)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <Check sx={{ color: '#00e5ff' }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Smart Automation
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    AI-powered automation handles repetitive tasks so you can focus on what matters most.
                  </Typography>
                </Box>
              </Box>

              {/* Benefit 4 */}
              <Box sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 3
              }}>
                <Box sx={{
                  minWidth: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'rgba(0, 229, 255, 0.1)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <Check sx={{ color: '#00e5ff' }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Personalized Experience
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    The more you use Taskly.AI, the better it understands your workflow and preferences.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* SECTION 4: CTA */}
      <Box sx={{
        width: '100%',
        minHeight: '80vh',
        background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* Animated background */}
        <Box
          component={motion.div}
          animate={{
            rotate: [0, 360],
            opacity: [0.1, 0.15, 0.1]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '150%',
            height: '150%',
            transform: 'translate(-50%, -50%)',
            background: 'conic-gradient(from 0deg at 50% 50%, rgba(0, 229, 255, 0) 0deg, rgba(0, 229, 255, 0.1) 90deg, rgba(0, 229, 255, 0) 180deg, rgba(41, 121, 255, 0.1) 270deg, rgba(0, 229, 255, 0) 360deg)',
            filter: 'blur(60px)',
            zIndex: 0
          }}
        />

        {/* Content */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          sx={{
            width: '100%',
            maxWidth: '1000px',
            textAlign: 'center',
            px: { xs: 4, md: 8 },
            py: { xs: 10, md: 0 },
            position: 'relative',
            zIndex: 1
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.5rem', md: '4.5rem' },
              fontWeight: 800,
              mb: 4,
              background: 'linear-gradient(45deg, #00e5ff, #2979ff, #00e5ff)',
              backgroundSize: '200% 200%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: 'gradient 8s ease infinite'
            }}
          >
            Ready to Boost Your Productivity?
          </Typography>

          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: '1.2rem', md: '1.8rem' },
              color: 'rgba(255, 255, 255, 0.7)',
              mb: 8,
              maxWidth: '800px',
              mx: 'auto'
            }}
          >
            Join thousands of users who have transformed their task management experience with our AI-powered assistant.
          </Typography>

          <motion.div
            whileHover={{
              scale: 1.05,
              transition: { duration: 0.3 }
            }}
          >
            <Button
              variant="contained"
              size="large"
              endIcon={
                <motion.div
                  animate={{ x: [0, 5, 0] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    repeatType: 'loop',
                    ease: "easeInOut",
                    repeatDelay: 1
                  }}
                >
                  <ArrowForward />
                </motion.div>
              }
              onClick={() => navigate('/register')}
              sx={{
                py: 3,
                px: 8,
                background: 'linear-gradient(45deg, #00e5ff 30%, #2979ff 90%)',
                boxShadow: '0 0 30px rgba(0, 229, 255, 0.5)',
                borderRadius: '50px',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                '&:hover': {
                  boxShadow: '0 0 50px rgba(0, 229, 255, 0.8)',
                  transform: 'translateY(-3px)',
                  transition: 'all 0.3s ease'
                }
              }}
            >
              Get Started Now
            </Button>
          </motion.div>

          {/* Footer */}
          <Box sx={{ mt: 12, opacity: 0.7 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', mb: 2 }}>
              © {new Date().getFullYear()} Taskly.AI. All rights reserved.
            </Typography>
            
            {/* GitHub Link */}
            <Box 
              component="a"
              href="https://github.com/akshaykarthicks"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                color: 'rgba(255, 255, 255, 0.6)',
                textDecoration: 'none',
                fontSize: '0.9rem',
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: '#00e5ff',
                  transform: 'translateY(-2px)',
                }
              }}
            >
              <GitHub sx={{ fontSize: '1.2rem' }} />
              <Typography variant="body2" sx={{ fontSize: 'inherit' }}>
                akshaykarthicks
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LandingPage;